import { useCallback, useEffect, useRef, useState } from "react";

// Small localStorage cache for football data — saves mobile data and survives reloads/offline.
const PREFIX = "bbk_fbc:";
const MAX_KEYS = 40;

export const readCache = (k) => { try { return JSON.parse(localStorage.getItem(PREFIX + k)); } catch { return null; } };

const prune = (aggressive) => {
  const keys = Object.keys(localStorage).filter((x) => x.startsWith(PREFIX));
  const drop = aggressive ? Math.ceil(keys.length / 2) : keys.length - MAX_KEYS;
  if (drop <= 0) return;
  keys.map((k) => [k, (() => { try { return JSON.parse(localStorage.getItem(k))?.t || 0; } catch { return 0; } })()])
    .sort((a, b) => a[1] - b[1]).slice(0, drop).forEach(([k]) => localStorage.removeItem(k));
};

export const writeCache = (k, data) => {
  try { localStorage.setItem(PREFIX + k, JSON.stringify({ t: Date.now(), data })); prune(false); }
  catch { prune(true); }
};

const fromStore = (key) => {
  const c = key && readCache(key);
  return c ? { data: c.data, savedAt: c.t, fromCache: true, error: false, loading: false } : { data: null, savedAt: 0, fromCache: false, error: false, loading: !!key };
};

// Serve cached data instantly, refetch only when older than ttl; optional visible-tab polling.
export function useCached(key, fetcher, ttlMs, poll = 0) {
  const [st, setSt] = useState(() => fromStore(key));
  const fRef = useRef(fetcher);
  fRef.current = fetcher;
  const keyRef = useRef(key);
  keyRef.current = key;

  const load = useCallback(async (force) => {
    if (!key) return;
    const c = readCache(key);
    if (!force && c && Date.now() - c.t < ttlMs) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) { setSt((s) => ({ ...s, error: true, loading: false })); return; }
    setSt((s) => ({ ...s, loading: true }));
    try {
      const r = await fRef.current();
      writeCache(key, r.data);
      if (keyRef.current === key) setSt({ data: r.data, savedAt: Date.now(), fromCache: false, error: false, loading: false });
    } catch {
      if (keyRef.current === key) setSt((s) => ({ ...s, error: true, loading: false }));
    }
  }, [key, ttlMs]);

  useEffect(() => { setSt(fromStore(key)); load(false); }, [key, load]);

  useEffect(() => {
    if (!poll) return undefined;
    const id = setInterval(() => { if (document.visibilityState === "visible") load(true); }, poll);
    const onVis = () => { if (document.visibilityState === "visible") load(false); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [poll, load]);

  return { ...st, reload: () => load(true) };
}

export const timeAgo = (iso, nowMs = Date.now()) => {
  if (!iso) return "";
  const t = typeof iso === "number" ? iso : new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((nowMs - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60); if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  const mo = Math.round(d / 30); if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const y = Math.round(d / 365); return `${y} year${y === 1 ? "" : "s"} ago`;
};
